import {
    Address,
    Contract,
    rpc as SorobanRpc,
    TransactionBuilder,
    nativeToScVal,
    xdr,
    Operation,
} from '@stellar/stellar-sdk/no-axios';
import { humanizeEvents } from '@stellar/stellar-base/lib/events';
import { authorizeEntry } from '@stellar/stellar-base/lib/auth';
import {
    signStellarTransaction,
    signStellarAuthEntry,
    assertWalletSupportsSorobanAuthEntrySigning,
    ensureKitInitialized,
    StellarWalletsKit,
} from '../services/stellarWalletsKit';

/**
 * Soroban entrypoints for the confidential-mirror contract (`audit/stellar-repo/contract/contracts/confidential-mirror`).
 * Every user method takes an explicit `owner` / `sender` `Address` plus `owner.require_auth()` — the invoker must match.
 * Callers still pass the same `methodArgs` as the EVM ethers path; we prepend `context.source` where `prependInvokerAddress` is set.
 */
const METHOD_MAP = {
    createConfidentialAccount: {
        soroban: 'create_confidential_account',
        argTypes: ['address', 'bytes'],
        /** Prepend `context.source` (G…) as first SC `Address` arg; caller still passes `[pubkeyBytes]`. */
        prependInvokerAddress: true,
    },
    /** `deposit(env, owner, token, plain_amount)` — `plain_amount` is ledger units (amount × 100), same as EVM `plainAmount`. */
    deposit: {
        soroban: 'deposit',
        argTypes: ['address', 'address', 'i128'],
        prependInvokerAddress: true,
    },
    /** `transfer_confidential(env, sender, recipient, token, proof, use_offchain_verify)`. */
    transferConfidential: {
        soroban: 'transfer_confidential',
        argTypes: ['address', 'address', 'address', 'bytes', 'bool'],
        prependInvokerAddress: true,
    },
    /** `apply_pending(env, owner)`. */
    applyPending: {
        soroban: 'apply_pending',
        argTypes: ['address'],
        prependInvokerAddress: true,
    },
    /** `withdraw(env, owner, token, plain_amount, proof, use_offchain_verify)`. */
    withdraw: {
        soroban: 'withdraw',
        argTypes: ['address', 'address', 'i128', 'bytes', 'bool'],
        prependInvokerAddress: true,
    },
};

function normalizeBytes(value) {
    if (value instanceof Uint8Array) return value;
    if (typeof value === 'string') {
        if (value.startsWith('0x')) {
            const hex = value.slice(2);
            const pairs = hex.match(/.{1,2}/g) || [];
            return Uint8Array.from(pairs.map((p) => parseInt(p, 16)));
        }
        return new TextEncoder().encode(value);
    }
    if (Array.isArray(value)) return Uint8Array.from(value);
    throw new Error('Unsupported bytes argument');
}

function toScVal(value, type) {
    switch (type) {
        case 'address':
            return Address.fromString(String(value)).toScVal();
        case 'i128':
            return nativeToScVal(BigInt(value?.toString?.() ?? value), { type: 'i128' });
        case 'bool':
            return nativeToScVal(Boolean(value), { type: 'bool' });
        case 'bytes':
            return xdr.ScVal.scvBytes(normalizeBytes(value));
        default:
            return nativeToScVal(value);
    }
}

async function walletSignTxXdr({ xdr: txXdr, networkPassphrase, source }) {
    // Every Stellar wallet connection now flows through the Stellar Wallets
    // Kit, so we no longer need per-wallet signing branches — the kit picks
    // the selected module (Freighter, Hana, xBull, ...) internally.
    ensureKitInitialized();
    const res = await signStellarTransaction(txXdr, {
        networkPassphrase,
        address: source,
    });
    if (!res || !res.signedTxXdr) {
        throw new Error('Stellar Wallets Kit failed to sign the transaction');
    }
    return res.signedTxXdr;
}

function summarizeOpResults(results) {
    if (!results || !results.length) return 'no op results';
    const parts = [];
    for (let i = 0; i < results.length; i += 1) {
        const r = results[i];
        try {
            const code = r.switch().name;
            if (code === 'opInner') {
                const trc = r.tr();
                const opn = trc.switch().name;
                if (opn === 'invokeHostFunction') {
                    const ihr = trc.invokeHostFunctionResult();
                    parts.push(`op${i}=invokeHostFunction:${ihr.switch().name}`);
                } else {
                    parts.push(`op${i}=${opn}`);
                }
            } else {
                parts.push(`op${i}=${code}`);
            }
        } catch (e) {
            parts.push(`op${i}=?(${e?.message || e})`);
        }
    }
    return parts.join(', ');
}

/** Best-effort decode of `TransactionResult` / fee-bump inner result for UI logs. */
function summarizeTransactionResult(tr) {
    try {
        const txr = tr.result();
        const top = txr.switch().name;
        if (top === 'txFailed' && typeof txr.results === 'function') {
            return `txFailed(${summarizeOpResults(txr.results())})`;
        }
        if (
            (top === 'txFeeBumpInnerFailed' || top === 'txFeeBumpInnerSuccess') &&
            typeof txr.innerResultPair === 'function'
        ) {
            const inner = txr.innerResultPair().result();
            const ir = inner.result();
            const innerName = ir.switch().name;
            if (innerName === 'txFailed' && typeof ir.results === 'function') {
                return `${top}(${summarizeOpResults(ir.results())})`;
            }
            return `${top}(${innerName})`;
        }
        return top;
    } catch (e) {
        return `txResult(${e?.message || e})`;
    }
}

function formatSorobanSendError(sent) {
    const parts = [];
    if (sent?.error) parts.push(`rpc: ${String(sent.error)}`);
    if (sent?.status) parts.push(`status: ${sent.status}`);

    if (sent?.errorResult) {
        try {
            parts.push(summarizeTransactionResult(sent.errorResult));
        } catch (e) {
            parts.push(`txResult: ${e?.message || e}`);
        }
    }

    if (sent?.diagnosticEvents?.length) {
        try {
            const friendly = humanizeEvents(sent.diagnosticEvents);
            friendly.forEach((ev, idx) => {
                parts.push(
                    `diag[${idx}] ${JSON.stringify({
                        contractId: ev.contractId,
                        topics: ev.topics,
                        data: ev.data,
                    })}`
                );
            });
        } catch (e) {
            parts.push(`diagnostics: ${e?.message || e}`);
        }
    }

    const joined = parts.join(' | ');
    if (joined) return joined;
    try {
        const raw = sent?.errorResult?.toXDR?.('base64');
        if (raw) return `rawResultXdr: ${raw}`;
    } catch (_) {}
    return 'Soroban sendTransaction rejected (no details)';
}

function withWalletSpecificSorobanHint(detail) {
    const normalized = String(detail || '');
    if (!/txBadAuth/i.test(normalized)) return normalized;

    let walletId = null;
    try {
        ensureKitInitialized();
        walletId = StellarWalletsKit.selectedModule?.productId || null;
    } catch (_) {
        walletId = null;
    }

    if (walletId === 'lobstr') {
        return (
            `${normalized} ` +
            'LOBSTR returned an invalid auth signature for this Soroban transaction on testnet. ' +
            'In LOBSTR Signer, switch to Stellar Testnet and retry. If it still fails, use Freighter or Hana for account creation.'
        );
    }

    return normalized;
}

function readSorobanTransactionData(preppedTx) {
    const inner = preppedTx.toEnvelope().v1().tx();
    const ext = inner.ext();
    if (typeof ext.sorobanData === 'function') {
        return ext.sorobanData();
    }
    const v = typeof ext.value === 'function' ? ext.value() : null;
    if (v) return v;
    throw new Error('Prepared transaction is missing Soroban extension data');
}

function needsInvokerAddressAuthSigning(authEntries, sourceG) {
    if (!authEntries?.length) return false;
    for (let i = 0; i < authEntries.length; i += 1) {
        const entry = authEntries[i];
        const cred = entry.credentials();
        if (cred.switch().name !== 'sorobanCredentialsAddress') continue;
        const sig = cred.address().signature();
        if (sig.switch().name !== 'scvVoid') continue;
        const who = Address.fromScAddress(cred.address().address()).toString();
        if (who === sourceG) return true;
    }
    return false;
}

/**
 * After `prepareTransaction`, simulation may attach `SorobanAuthorizationEntry`
 * rows with void signatures for the invoker (`source`). Those must be signed
 * via SEP-43 `signAuthEntry` before `signTransaction`, or the network returns
 * `txBadAuth`.
 */
async function signPreparedInvokerAuthEntries(server, prepared, { networkPassphrase, source }) {
    const op0 = prepared.operations[0];
    if (!op0 || op0.type !== 'invokeHostFunction') return prepared;

    const initialAuth = op0.auth || [];
    if (!needsInvokerAddressAuthSigning(initialAuth, source)) return prepared;

    assertWalletSupportsSorobanAuthEntrySigning();

    const { sequence } = await server.getLatestLedger();
    const validUntil = Number(sequence) + 100;

    const auth = initialAuth.map((e) => xdr.SorobanAuthorizationEntry.fromXDR(e.toXDR()));
    let changed = false;

    for (let i = 0; i < auth.length; i += 1) {
        const entry = auth[i];
        const cred = entry.credentials();
        if (cred.switch().name !== 'sorobanCredentialsAddress') continue;
        const addrC = cred.address();
        if (addrC.signature().switch().name !== 'scvVoid') continue;
        const entryAddr = Address.fromScAddress(addrC.address()).toString();
        if (entryAddr !== source) continue;

        /* eslint-disable no-await-in-loop */
        const signed = await authorizeEntry(
            entry,
            async (preimage) => {
                const b64 = preimage.toXDR('base64');
                const res = await signStellarAuthEntry(b64, { networkPassphrase, address: source });
                if (!res || !res.signedAuthEntry) {
                    throw new Error('Wallet did not return signedAuthEntry for Soroban authorization');
                }
                const s = res.signedAuthEntry;
                return typeof s === 'string' ? Buffer.from(s, 'base64') : Buffer.from(s);
            },
            validUntil,
            networkPassphrase
        );
        /* eslint-enable no-await-in-loop */
        auth[i] = signed;
        changed = true;
    }

    if (!changed) return prepared;

    const sorobanData = readSorobanTransactionData(prepared);

    const tb = TransactionBuilder.cloneFrom(prepared, {
        fee: prepared.fee,
        networkPassphrase,
        sorobanData,
    });
    tb.clearOperations();
    tb.addOperation(
        Operation.invokeHostFunction({
            func: op0.func,
            auth,
            source: op0.source,
        })
    );
    return tb.build();
}

async function waitForTx(server, hash) {
    for (let i = 0; i < 25; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const tx = await server.getTransaction(hash);
        if (tx.status === SorobanRpc.Api.GetTransactionStatus.SUCCESS) return tx;
        if (tx.status === SorobanRpc.Api.GetTransactionStatus.FAILED) {
            const detail = tx.resultXdr ? String(tx.resultXdr) : 'unknown error';
            throw new Error(`Soroban transaction failed on-chain: ${detail}`);
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 1200));
    }
    throw new Error('Timed out waiting for Soroban transaction confirmation');
}

export async function submitSorobanContractTx(methodName, methodArgs = [], context) {
    const { rpcUrl, networkPassphrase, contractId, source } = context || {};
    if (!rpcUrl || !networkPassphrase || !contractId || !source) {
        throw new Error('Missing Stellar transaction context (rpc/passphrase/contract/source)');
    }
    const methodMeta = METHOD_MAP[methodName];
    if (!methodMeta) {
        throw new Error(`Unsupported Soroban method mapping for ${methodName}`);
    }

    const argTypes = methodMeta.argTypes || [];
    let scInputValues = methodArgs.slice();
    if (methodMeta.prependInvokerAddress) {
        if (!source) {
            throw new Error(`Soroban ${methodName}: prependInvokerAddress requires context.source`);
        }
        if (scInputValues.length !== argTypes.length - 1) {
            throw new Error(
                `Soroban ${methodName}: expected ${argTypes.length - 1} argument(s) after invoker address, got ${methodArgs.length}`
            );
        }
        scInputValues = [source, ...scInputValues];
    }
    if (scInputValues.length !== argTypes.length) {
        throw new Error(
            `Soroban ${methodName}: expected ${argTypes.length} argument(s), got ${scInputValues.length}`
        );
    }
    const scArgs = scInputValues.map((arg, idx) => toScVal(arg, argTypes[idx]));

    const server = new SorobanRpc.Server(rpcUrl, { allowHttp: rpcUrl.startsWith('http://') });
    const account = await server.getAccount(source);
    const contract = new Contract(contractId);
    const op = contract.call(methodMeta.soroban, ...scArgs);
    const tx = new TransactionBuilder(account, {
        fee: '100000',
        networkPassphrase,
    })
        .addOperation(op)
        .setTimeout(60)
        .build();

    let prepared = await server.prepareTransaction(tx);
    prepared = await signPreparedInvokerAuthEntries(server, prepared, { networkPassphrase, source });
    const signedXdr = await walletSignTxXdr({
        xdr: prepared.toXDR(),
        networkPassphrase,
        source,
    });
    const signed = TransactionBuilder.fromXDR(signedXdr, networkPassphrase);
    const sent = await server.sendTransaction(signed);
    const sendStatus = String(sent.status || '').toUpperCase();
    if (sendStatus === 'ERROR' || sendStatus === 'NOT_FOUND' || sendStatus === 'DUPLICATE') {
        const detail = withWalletSpecificSorobanHint(formatSorobanSendError(sent));
        // eslint-disable-next-line no-console
        console.error('[Soroban] sendTransaction rejected:', detail, sent);
        throw new Error(`Soroban sendTransaction failed: ${detail}`);
    }

    const txHash = sent.hash;
    return {
        hash: txHash,
        wait: async () => {
            const finalTx = await waitForTx(server, txHash);
            return {
                transactionHash: txHash,
                status: 1,
                logs: [],
                soroban: finalTx,
            };
        },
    };
}
